/**
 * A VR interface panel: a quad in the scene whose texture is a 2D canvas (vrPanelKit draws it),
 * and the hit test that turns a controller ray into a point on that canvas.
 *
 * - The quad faces +Z; faceTowards() turns it to the visitor's head.
 * - Panels draw in the VR UI layer (vrUiLayer), after the scene and without depth testing, so a
 *   wall or a splat between the visitor and a menu never hides it.
 * - The texture is re-uploaded only when the canvas is redrawn (commit()).
 */
import * as pc from 'playcanvas';

/** Draw order of the VR interface's parts in vrUiLayer (back to front). */
export const VR_DRAW_ORDER = { label: 5, menu: 10, keyboard: 11, overlay: 12, popup: 20, video: 21, banner: 30, wrist: 40 } as const;

/** The layer every VR panel draws in: after the world, on top of it. Made once per app. */
const uiLayers = new WeakMap<pc.AppBase, pc.Layer>();

export function vrUiLayer(app: pc.AppBase): pc.Layer {
    let layer = uiLayers.get(app);
    if (!layer) {
        // Panels set their own draw order (a popup over a menu, the wrist menu over both).
        layer = new pc.Layer({ name: 'StorySplat VR UI', opaqueSortMode: pc.SORTMODE_MANUAL, transparentSortMode: pc.SORTMODE_MANUAL });
        app.scene.layers.push(layer);
        uiLayers.set(app, layer);
    }
    return layer;
}

/** The quad's mesh: 1 × 1 in XY, facing +Z, with the canvas's top row at the top. */
function quadMesh(device: pc.GraphicsDevice): pc.Mesh {
    const mesh = new pc.Mesh(device);
    mesh.setPositions([-0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, 0.5, 0]);
    mesh.setNormals([0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1]);
    // Canvas row 0 is texture row 0 (v = 0): the top vertices get v = 0.
    mesh.setUvs(0, [0, 1, 1, 1, 1, 0, 0, 0]);
    mesh.setIndices([0, 1, 2, 0, 2, 3]);
    mesh.update();
    return mesh;
}

/** A point on a panel's canvas where a ray hits it. */
export interface PanelRayHit {
    /** Canvas pixels. */
    x: number;
    y: number;
    /** Along the ray, in metres. */
    distance: number;
}

const _inverse = new pc.Mat4();
const _origin = new pc.Vec3();
const _dir = new pc.Vec3();
const _end = new pc.Vec3();
const _lookAt = new pc.Vec3();

export class VrPanel {
    readonly entity: pc.Entity;
    readonly canvas: HTMLCanvasElement;
    readonly ctx: CanvasRenderingContext2D | null;
    /** Size in metres. */
    width: number;
    height: number;
    private readonly texture: pc.Texture;
    private readonly material: pc.StandardMaterial;
    private readonly mesh: pc.Mesh;
    private destroyed = false;

    constructor(app: pc.AppBase, name: string, metres: { width: number; height: number }, pixels: { width: number; height: number }, parent?: pc.Entity, drawOrder = 0) {
        this.width = metres.width;
        this.height = metres.height;
        this.canvas = document.createElement('canvas');
        this.canvas.width = pixels.width;
        this.canvas.height = pixels.height;
        this.ctx = this.canvas.getContext('2d');
        const device = app.graphicsDevice;
        this.texture = new pc.Texture(device, {
            name: `${name}-texture`,
            width: pixels.width,
            height: pixels.height,
            format: pc.PIXELFORMAT_RGBA8,
            mipmaps: true,
            minFilter: pc.FILTER_LINEAR_MIPMAP_LINEAR,
            magFilter: pc.FILTER_LINEAR,
            addressU: pc.ADDRESS_CLAMP_TO_EDGE,
            addressV: pc.ADDRESS_CLAMP_TO_EDGE,
        });
        const material = new pc.StandardMaterial();
        material.diffuse = new pc.Color(0, 0, 0);
        material.emissive = new pc.Color(1, 1, 1);
        material.emissiveMap = this.texture;
        material.opacityMap = this.texture;
        material.opacityMapChannel = 'a';
        material.useLighting = false;
        material.useFog = false;
        material.useSkybox = false;
        material.useTonemap = false;
        material.blendType = pc.BLEND_NORMAL;
        material.depthTest = false;
        material.depthWrite = false;
        material.cull = pc.CULLFACE_NONE;
        material.update();
        this.material = material;
        this.mesh = quadMesh(device);
        this.entity = new pc.Entity(name);
        const instance = new pc.MeshInstance(this.mesh, material);
        instance.drawOrder = drawOrder;
        this.entity.addComponent('render', {
            meshInstances: [instance],
            layers: [vrUiLayer(app).id],
            castShadows: false,
            receiveShadows: false,
        });
        this.entity.setLocalScale(metres.width, metres.height, 1);
        this.entity.enabled = false;
        (parent ?? app.root).addChild(this.entity);
    }

    get visible(): boolean {
        return this.entity.enabled;
    }

    set visible(on: boolean) {
        if (!this.destroyed) this.entity.enabled = on;
    }

    /** Upload the canvas after drawing. */
    commit(): void {
        if (this.destroyed) return;
        this.texture.setSource(this.canvas);
    }

    /** Resize in metres (the canvas keeps its pixels). */
    setSize(width: number, height: number): void {
        this.width = width;
        this.height = height;
        this.entity.setLocalScale(width, height, 1);
    }

    /** Turn the front (+Z) toward `point`, upright. */
    faceTowards(point: pc.Vec3): void {
        const pos = this.entity.getPosition();
        // lookAt points -Z at its target: aim it away from the point so +Z faces it.
        _lookAt.set(pos.x * 2 - point.x, pos.y * 2 - point.y, pos.z * 2 - point.z);
        this.entity.lookAt(_lookAt, pc.Vec3.UP);
    }

    /**
     * Put the panel `distance` metres in front of the head (horizontally), `drop` metres below the
     * eyes, facing them.
     */
    placeInFront(head: pc.Entity, distance: number, drop: number, sideways = 0): void {
        const eye = head.getPosition();
        const forward = head.forward;
        let fx = forward.x, fz = forward.z;
        const flat = Math.hypot(fx, fz);
        if (flat < 1e-4) {
            // Looking straight up or down: use the head's up as the heading.
            fx = -head.up.x;
            fz = -head.up.z;
        }
        const len = Math.hypot(fx, fz) || 1;
        fx /= len;
        fz /= len;
        // Right of the heading: (-fz, fx) turned the other way.
        const rx = -fz, rz = fx;
        this.entity.setPosition(eye.x + fx * distance - rx * sideways, eye.y - drop, eye.z + fz * distance - rz * sideways);
        this.faceTowards(eye);
    }

    /** Where a world ray hits the panel's front or back, in canvas pixels; null when it misses. */
    hit(origin: pc.Vec3, direction: pc.Vec3, out: PanelRayHit): PanelRayHit | null {
        if (!this.entity.enabled || this.destroyed) return null;
        _inverse.copy(this.entity.getWorldTransform()).invert();
        _inverse.transformPoint(origin, _origin);
        _end.copy(origin).add(direction);
        _inverse.transformPoint(_end, _dir);
        _dir.sub(_origin);
        if (Math.abs(_dir.z) < 1e-9) return null;
        const t = -_origin.z / _dir.z;
        if (t <= 0) return null;
        const lx = _origin.x + _dir.x * t;
        const ly = _origin.y + _dir.y * t;
        if (lx < -0.5 || lx > 0.5 || ly < -0.5 || ly > 0.5) return null;
        out.x = (lx + 0.5) * this.canvas.width;
        out.y = (0.5 - ly) * this.canvas.height;
        // t is in local units of the transformed direction (a unit world direction): world distance.
        out.distance = t;
        return out;
    }

    /** The world point at canvas pixel (x, y) on the panel. */
    pointAt(x: number, y: number, out: pc.Vec3): pc.Vec3 {
        _origin.set(x / this.canvas.width - 0.5, 0.5 - y / this.canvas.height, 0);
        return this.entity.getWorldTransform().transformPoint(_origin, out);
    }

    destroy(): void {
        if (this.destroyed) return;
        this.destroyed = true;
        this.entity.destroy();
        this.mesh.destroy();
        this.material.destroy();
        this.texture.destroy();
    }
}
